/**
 * AP-39 12.4 — **Einstellungen des Posteingangs** (`/inbox/settings`, `inbox.settings.manage`).
 *
 * Phase C: automatisch zuordnen, automatische Mails ignorieren, Erledigte ausblenden nach … Tagen.
 * Phase H: die KI (`INBOX.AI`) — nur mit dem Baustein: Einwilligung, einschalten, Werte von selbst lesen.
 * Phase E: der Ordner in Microsoft 365 — verbinden, Konto, Pfad, letzte Prüfung, Störung, trennen.
 * Eingangsadresse und Absenderregeln kommen mit Phase D (zurückgestellt).
 * Gespeichert wird feldweise ohne Knopf (AP-36).
 */
import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { ArrowLeft, FolderSync, Loader2, Settings, Sparkles } from 'lucide-react'
import { Hausregeln } from '@/components/ki/Hausregeln'
import { api } from '@/lib/axios'
import { apiErrorText } from '@/lib/api-error'
import { bestaetige } from '@/lib/dialoge'
import { fmtDateTime } from '@/lib/format-date'
import { pageWide } from '@/lib/form-styles'
import { Abschnitt, FeldZeile, SchalterZeile, feldKlasse, mitStand, useFeld, useSpeicherstelle, type Datensatz, type Speicherstelle } from '@/lib/speichern'

interface Einstellungen {
  autoAssign: boolean
  ignoreAutoMails: boolean
  hideDoneAfterDays: number
  updatedAt: string | null
  /** 12.4 „Ordner" (5.2) — der eingerichtete Ordner in Microsoft 365 */
  folder: { available: boolean; connected: boolean; account: string | null; accountName: string | null; path: string | null; lastCheckAt: string | null; error: string | null; errorSince: string | null }
  /** 12.4 „KI (mit `.AI`)" — `null` ohne den Baustein: Was nicht gebucht ist, wird nicht angeboten. */
  ai: { booked: boolean; tenantAi: boolean; enabled: boolean; autoRead: boolean; consentCurrent: boolean; consentAt: string | null; ready: boolean; consentText: string; consentVersion: string } | null
}

/** Warum die Rückkehr von Microsoft nicht geklappt hat (`?folder=error&code=…`). */
const RUECKKEHR_FEHLER: Record<string, string> = {
  consent_denied: 'Die Zustimmung bei Microsoft wurde abgelehnt — der Ordner ist nicht verbunden.',
  admin_consent_required: 'Microsoft verlangt für dieses Recht die Zustimmung eines Administrators eures Verzeichnisses.',
  not_allowed: 'Du darfst den Ordner nicht (mehr) verbinden.',
  token_exchange_failed: 'Die Anmeldung bei Microsoft hat nicht geklappt — bitte noch einmal versuchen.',
  setup_failed: 'Die Anmeldung hat geklappt, aber der Ordner ließ sich in Microsoft 365 nicht anlegen. Bitte noch einmal verbinden.',
  state_invalid: 'Die Anmeldung ist abgelaufen — bitte noch einmal verbinden.',
}

/** Die Schalter der KI stehen in der Antwort unter `ai`, geschrieben werden sie als `aiEnabled` und `aiAutoRead`. */
const alsDatensatz = (d: Einstellungen | null) => (d ? ({ ...d, aiEnabled: d.ai?.enabled ?? false, aiAutoRead: d.ai?.autoRead ?? true } as unknown as Datensatz) : null)

export default function InboxSettingsPage() {
  const [daten, setDaten] = useState<Einstellungen | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [meldung, setMeldung] = useState<string | null>(null)
  const [arbeitet, setArbeitet] = useState(false)
  const [parameter, setParameter] = useSearchParams()

  // Rückkehr von Microsoft: einmal melden, dann die Parameter aus der Adresse nehmen.
  useEffect(() => {
    const folder = parameter.get('folder')
    if (!folder) return
    if (folder === 'connected') setMeldung('Der Ordner ist verbunden. Was du dort ablegst, steht nach etwa einer Minute im Posteingang.')
    else setFehler(RUECKKEHR_FEHLER[parameter.get('code') ?? ''] ?? 'Der Ordner ließ sich nicht verbinden.')
    setParameter({}, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    api
      .get<{ data: Einstellungen }>('/inbox/settings')
      .then((r) => setDaten(r.data.data))
      .catch((e) => setFehler(apiErrorText(e, 'Die Einstellungen ließen sich nicht laden.')))
  }, [])

  // Ein Datensatz je geladenem Stand — nicht je Render (die Speicherstelle übernimmt jede neue Referenz).
  const datensatz = useMemo(() => alsDatensatz(daten), [daten])
  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: 'inboxSettings',
    bezeichnung: 'Einstellungen des Posteingangs',
    daten: datensatz,
    schreibe: async (patch, stand) => {
      const r = await api.patch<{ data: Einstellungen }>('/inbox/settings', patch, mitStand(stand))
      setDaten(r.data.data)
      return alsDatensatz(r.data.data)!
    },
    keepalive: { url: '/inbox/settings', method: 'PATCH' },
  }) as Speicherstelle<unknown>
  const auto = useFeld<boolean>(stelle, 'autoAssign', { art: 'schalter', label: 'Automatisch zuordnen' })
  const autoMails = useFeld<boolean>(stelle, 'ignoreAutoMails', { art: 'schalter', label: 'Automatische Mails ignorieren' })
  const tage = useFeld<number | null>(stelle, 'hideDoneAfterDays', { art: 'zahl', label: 'Erledigte ausblenden nach', pflicht: true })
  const ki = daten?.ai ?? null
  const kiAn = useFeld<boolean>(stelle, 'aiEnabled', { art: 'schalter', label: 'Dokumente mit KI lesen', gesperrt: !ki?.consentCurrent })
  const kiSelbst = useFeld<boolean>(stelle, 'aiAutoRead', { art: 'schalter', label: 'Werte von selbst lesen', gesperrt: !ki?.enabled })

  // ── Ordner (5.2): Verbinden, Prüfen und Trennen sind Handlungen — je ein Knopf mit dem Verb (AP-36). ──
  const ordner = daten?.folder ?? null
  const ordnerVerbinden = async () => {
    setFehler(null)
    setArbeitet(true)
    try {
      const r = await api.post<{ data: { authorizeUrl: string } }>('/inbox/settings/folder/connect')
      // Weiter zur Anmeldung bei Microsoft; zurück kommt der Browser auf diese Seite.
      window.location.assign(r.data.data.authorizeUrl)
    } catch (e) {
      setFehler(apiErrorText(e, 'Das Verbinden ließ sich nicht starten.'))
      setArbeitet(false)
    }
  }
  const ordnerPruefen = async () => {
    setFehler(null)
    setMeldung(null)
    setArbeitet(true)
    try {
      const r = await api.post<{ data: Einstellungen; meta: { taken: number } }>('/inbox/settings/folder/check')
      setDaten(r.data.data)
      if (!r.data.data.folder.error) setMeldung(r.data.meta.taken ? `${r.data.meta.taken} ${r.data.meta.taken === 1 ? 'Datei' : 'Dateien'} übernommen.` : 'Geprüft — im Ordner liegt nichts Neues.')
    } catch (e) {
      setFehler(apiErrorText(e, 'Das Prüfen hat nicht geklappt.'))
    } finally {
      setArbeitet(false)
    }
  }
  const ordnerTrennen = async () => {
    if (!(await bestaetige('Ordner trennen? Symphenia fragt ihn dann nicht mehr ab; der Ordner und seine Dateien in Microsoft 365 bleiben, wie sie sind.', { ok: 'Trennen' }))) return
    setFehler(null)
    setMeldung(null)
    try {
      const r = await api.delete<{ data: Einstellungen }>('/inbox/settings/folder')
      setDaten(r.data.data)
    } catch (e) {
      setFehler(apiErrorText(e, 'Das Trennen hat nicht geklappt.'))
    }
  }

  /** Einwilligen und Zurücknehmen sind Handlungen — ein Knopf mit dem Verb (AP-36). */
  const einwilligung = async (geben: boolean) => {
    if (!geben && !(await bestaetige('Einwilligung zurücknehmen? Die KI im Posteingang wird ausgeschaltet; schon gelesene Vorschläge bleiben stehen.', { ok: 'Zurücknehmen' }))) return
    setFehler(null)
    try {
      const r = await api.patch<{ data: Einstellungen }>('/inbox/settings', geben ? { aiConsent: true, aiEnabled: true } : { aiConsent: false })
      setDaten(r.data.data)
    } catch (e) {
      setFehler(apiErrorText(e, 'Das hat nicht geklappt.'))
    }
  }

  return (
    <div className={pageWide}>
      <div>
        <Link to="/inbox" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Posteingang
        </Link>
        <h1 className="mt-1 flex items-center gap-2 text-3xl font-bold text-foreground">
          <Settings className="h-7 w-7" aria-hidden /> Einstellungen des Posteingangs
        </h1>
      </div>
      {fehler && <div className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{fehler}</div>}
      {meldung && <div className="rounded-md bg-emerald-100 px-4 py-3 text-sm text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-100">{meldung}</div>}
      {daten && (
        <Abschnitt titel="Erkennen und zuordnen">
          <SchalterZeile
            feld={auto}
            label="Automatisch zuordnen"
            hinweis="Nur wenn alles eindeutig ist: genau eine unserer Belegnummern, die Art passt zum Beleg, der Absender ist der Partner des Belegs. Sonst steht das Dokument als „Vorschlag offen“ im Posteingang."
          />
          <SchalterZeile
            feld={autoMails}
            label="Automatische Mails ignorieren"
            hinweis="Abwesenheitsnotizen und Zustellberichte landen als „ignoriert“ im Posteingang und zählen nicht."
          />
          <FeldZeile label="Erledigte ausblenden nach" status={tage} hilfe="Erledigte und ignorierte Einträge verschwinden danach aus der Liste; der Filter „auch ältere Erledigte“ zeigt sie weiter.">
            <span className="inline-flex items-center gap-2">
              <input className={`${feldKlasse} w-24 text-right`} inputMode="numeric" {...tage.props} /> Tagen
            </span>
          </FeldZeile>
        </Abschnitt>
      )}
      {daten && ordner && (
        <Abschnitt titel="Ordner">
          <div className="space-y-3 text-sm">
            <p className="flex items-start gap-2 text-muted-foreground">
              <FolderSync className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden />
              <span>
                Ein Ordner in Microsoft 365 (OneDrive), den Symphenia jede Minute prüft: Was dort abgelegt oder hineingescannt wird, kommt in den Posteingang und liegt danach im Ordner unter „Übernommen“.
                Über den OneDrive-Client erscheint er im Explorer wie jeder andere Ordner.
              </span>
            </p>
            {!ordner.available && <p className="rounded-md bg-muted px-3 py-2">Microsoft 365 ist in dieser Installation nicht eingerichtet — der Ordner steht deshalb nicht zur Verfügung.</p>}
            {ordner.available && !ordner.connected && (
              <div className="rounded-md border border-border bg-muted/40 px-3 py-2">
                <p>
                  Ein Konto eures Betriebs — am besten das Büro-Konto — meldet sich bei Microsoft an. Symphenia legt in dessen OneDrive den Ordner <span className="font-medium">Apps / Symphenia / Posteingang</span> an und arbeitet nur darin.
                  Das Konto kann den Ordner danach für Kollegen freigeben.
                </p>
                <button type="button" disabled={arbeitet} onClick={() => void ordnerVerbinden()} className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1 font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
                  {arbeitet && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />} Ordner verbinden
                </button>
              </div>
            )}
            {ordner.connected && (
              <>
                {ordner.error && (
                  <p className="rounded-md bg-destructive/10 px-3 py-2 text-destructive">
                    Ordner nicht verbunden{ordner.errorSince ? ` — seit ${fmtDateTime(ordner.errorSince)}` : ''}. {ordner.error}
                  </p>
                )}
                <dl className="grid grid-cols-[9rem_1fr] gap-x-3 gap-y-1">
                  <dt className="text-muted-foreground">Konto</dt>
                  <dd>{[ordner.accountName, ordner.account].filter(Boolean).join(' · ') || '—'}</dd>
                  <dt className="text-muted-foreground">Ordner</dt>
                  <dd>{ordner.path ?? '—'}</dd>
                  <dt className="text-muted-foreground">Zuletzt geprüft</dt>
                  <dd>{ordner.lastCheckAt ? fmtDateTime(ordner.lastCheckAt) : 'noch nicht'}</dd>
                </dl>
                <div className="flex flex-wrap gap-2">
                  <button type="button" disabled={arbeitet} onClick={() => void ordnerPruefen()} className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1 hover:bg-muted disabled:opacity-60">
                    {arbeitet && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />} Jetzt prüfen
                  </button>
                  <button type="button" disabled={arbeitet} onClick={() => void ordnerVerbinden()} className="rounded-md border border-border px-3 py-1 hover:bg-muted disabled:opacity-60">
                    Neu verbinden
                  </button>
                  <button type="button" disabled={arbeitet} onClick={() => void ordnerTrennen()} className="rounded-md border border-border px-3 py-1 text-destructive hover:bg-destructive/10 disabled:opacity-60">
                    Trennen
                  </button>
                </div>
              </>
            )}
          </div>
        </Abschnitt>
      )}
      {daten && ki && (
        <Abschnitt titel="KI">
          <div className="space-y-2 text-sm">
            <p className="flex items-start gap-2 text-muted-foreground">
              <Sparkles className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden />
              <span>
                Die KI erkennt Art, Absender und Bezugsnummern von Scans und Dokumenten, bei denen die Regeln nicht weiterkommen, und liest die Zeilen von Angeboten und Auftragsbestätigungen.
                Sie ordnet nichts zu und bestätigt nichts: Was sie liest, steht als Vorschlag am Beleg.
              </span>
            </p>
            <div className="rounded-md border border-border bg-muted/40 px-3 py-2">
              <p>{ki.consentText}</p>
              <div className="mt-2 flex flex-wrap items-center gap-3">
                {ki.consentCurrent ? (
                  <>
                    <span className="text-muted-foreground">Eingewilligt{ki.consentAt ? ` am ${fmtDateTime(ki.consentAt)}` : ''}.</span>
                    <button type="button" onClick={() => void einwilligung(false)} className="rounded-md border border-border px-3 py-1 hover:bg-muted">
                      Einwilligung zurücknehmen
                    </button>
                  </>
                ) : (
                  <button type="button" onClick={() => void einwilligung(true)} className="rounded-md bg-primary px-3 py-1 font-medium text-primary-foreground hover:bg-primary/90">
                    Einwilligen und einschalten
                  </button>
                )}
              </div>
            </div>
            {!ki.tenantAi && (
              <p className="rounded-md bg-amber-100 px-3 py-2 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100">
                Die KI des Mandanten ist ausgeschaltet oder es fehlt ein Schlüssel — solange liest der Posteingang nichts mit KI.
              </p>
            )}
          </div>
          <SchalterZeile feld={kiAn} label="Dokumente mit KI lesen" hinweis="Aus heißt: kein Dokument aus dem Posteingang geht an den KI-Anbieter. Einzelne Einträge lassen sich mit „Nicht an die KI“ ausnehmen." />
          <SchalterZeile
            feld={kiSelbst}
            label="Werte von selbst lesen"
            hinweis="Sobald ein Angebot an einer Preisanfrage oder eine Auftragsbestätigung an einer Bestellung hängt, werden ihre Zeilen gelesen. Aus heißt: nur auf „Werte mit KI lesen“."
          />
        </Abschnitt>
      )}
      {/* AP-44: der Text des Betriebs, der beim Erkennen und beim Auslesen hinter dem festen Auftrag mitgeht */}
      {daten && ki && <Hausregeln pfad="/inbox/house-rules" />}
      <p className="text-sm text-muted-foreground">Die Eingangsadresse per E-Mail und ihre Absenderregeln sind zurückgestellt.</p>
    </div>
  )
}
